import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  LogIn,
  UserPlus,
  Mail,
  Lock,
  X,
  Eye,
  EyeOff,
  User,
  Phone,
  Home,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { authService } from '../lib/auth';
import { cognitoLogin, cognitoRegister, cognitoConfirmSignup, cognitoResendCode, isUserNotConfirmedError } from '../lib/cognito-auth';
import { logger } from '../services/logger.service';
import { toast } from 'sonner';

interface LoginProps {
  onLoginSuccess: () => void;
  onClose: () => void;
  onNavigate?: (page: string) => void;
}

type View = 'login' | 'seller-signup' | 'seller-login' | 'seller-success' | 'verify-email';

export function Login({ onLoginSuccess, onClose, onNavigate }: LoginProps) {
  const [view, setView] = useState<View>('login');

  // Main login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Seller sign-up state
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPhone, setSignupPhone] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirm, setShowSignupConfirm] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');

  // Seller login state
  const [sellerLoginEmail, setSellerLoginEmail] = useState('');
  const [sellerLoginPassword, setSellerLoginPassword] = useState('');
  const [sellerLoginLoading, setSellerLoginLoading] = useState(false);
  const [sellerLoginError, setSellerLoginError] = useState('');
  const [showSellerLoginPassword, setShowSellerLoginPassword] = useState(false);

  // Email verification state
  const [verifyEmail, setVerifyEmail] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // ─── Email verification ───
  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerifyError('');
    setVerifyLoading(true);

    try {
      const { user } = await cognitoConfirmSignup(verifyEmail, verifyCode, verifyPassword);

      toast.success(`Email verified! Welcome, ${user.name}!`, {
        description: 'Your account is now active',
      });

      setView('seller-success');
      setTimeout(() => {
        onLoginSuccess();
      }, 1500);
    } catch (err: any) {
      logger.error('Email verification error', err as Error, undefined, 'Login');
      setVerifyError(err.message || 'Verification failed. Please check your code and try again.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setResendLoading(true);

    try {
      await cognitoResendCode(verifyEmail);
      toast.success('Verification code resent!', {
        description: `A new code has been sent to ${verifyEmail}`,
      });
      // Start a 60-second cooldown
      setResendCooldown(60);
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      logger.error('Resend code error', err as Error, undefined, 'Login');
      toast.error('Failed to resend code', {
        description: err.message || 'Please try again later',
      });
    } finally {
      setResendLoading(false);
    }
  };

  // ─── Main portal login (Cognito) ───
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setLoginError('');

    try {
      // Trim whitespace from credentials
      const { user } = await cognitoLogin(username.trim(), password.trim());
      toast.success(`Welcome back, ${user.name}!`, {
        description: `Logged in as ${user.role.replace('-', ' ')}`,
      });
      onLoginSuccess();
    } catch (err: any) {
      logger.error('Login failed', err as Error, undefined, 'Login');

      // If the user hasn't confirmed their email, redirect to verification
      if (isUserNotConfirmedError(err)) {
        setVerifyEmail(username);
        setVerifyPassword(password);
        setVerifyError('');
        setVerifyCode('');
        setView('verify-email');
        toast.info('Email verification required', {
          description: 'Please enter the verification code sent to your email',
        });
      } else {
        // F-014 fix: backend may return 500 for wrong password (F-007) — always show a friendly message
        const rawMsg: string = err.message || '';
        const isServerError = /server.*error|internal|500|encountered.*error|unexpected/i.test(rawMsg);
        const displayMsg = isServerError
          ? 'Invalid email or password. Please try again.'
          : (rawMsg || 'Invalid credentials. Please check your email and password.');
        setLoginError(displayMsg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Seller registration (Cognito) ───
  const handleSellerSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupError('');

    if (signupPassword !== signupConfirm) {
      setSignupError('Passwords do not match');
      return;
    }
    if (signupPassword.length < 8) {
      setSignupError('Password must be at least 8 characters');
      return;
    }

    setSignupLoading(true);

    try {
      const { user } = await cognitoRegister({
        email: signupEmail,
        password: signupPassword,
        name: signupName,
        group: 'seller',
        phone: signupPhone || undefined,
      });

      setView('seller-success');

      toast.success(`Welcome, ${user.name}!`, {
        description: 'Your seller account has been created via Cognito',
      });

      setTimeout(() => {
        onLoginSuccess();
      }, 1500);
    } catch (err: any) {
      logger.error('Seller registration error', err as Error, undefined, 'Login');

      // If registration succeeded but email needs confirmation, redirect to verification
      if (isUserNotConfirmedError(err)) {
        setVerifyEmail(signupEmail);
        setVerifyPassword(signupPassword);
        setVerifyError('');
        setVerifyCode('');
        setView('verify-email');
        toast.info('Check your email!', {
          description: 'A verification code has been sent to your email address',
        });
      } else {
        setSignupError(err.message || 'Registration failed. Please try again.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  // ─── Seller login (Cognito) ───
  const handleSellerLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSellerLoginError('');

    setSellerLoginLoading(true);

    try {
      // Trim whitespace from credentials
      const { user } = await cognitoLogin(sellerLoginEmail.trim(), sellerLoginPassword.trim());

      setView('seller-success');

      toast.success(`Welcome, ${user.name}!`, {
        description: 'You have successfully logged in to your seller account',
      });

      setTimeout(() => {
        onLoginSuccess();
      }, 1500);
    } catch (err: any) {
      logger.error('Seller login error', err as Error, undefined, 'Login');

      if (isUserNotConfirmedError(err)) {
        setVerifyEmail(sellerLoginEmail);
        setVerifyPassword(sellerLoginPassword);
        setVerifyError('');
        setVerifyCode('');
        setView('verify-email');
        toast.info('Email verification required', {
          description: 'Please enter the verification code sent to your email',
        });
      } else {
        setSellerLoginError(err.message || 'Login failed. Please try again.');
      }
    } finally {
      setSellerLoginLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 bg-card/95 backdrop-blur-xl shadow-2xl">
          <CardHeader className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-4 top-4 hover:bg-muted/50"
              onClick={onClose}
              data-testid="auth-modal-close"
            >
              <X className="w-4 h-4" />
            </Button>

            <AnimatePresence mode="wait">
              {view === 'login' && (
                <motion.div key="login-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <CardTitle className="text-2xl flex items-center gap-2">
                    <LogIn className="w-6 h-6 text-primary" />
                    Sign In
                  </CardTitle>
                  <CardDescription>
                    Access your Something Property portal
                  </CardDescription>
                </motion.div>
              )}

              {view === 'seller-signup' && (
                <motion.div key="signup-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#FDB022] flex items-center justify-center">
                      <Home className="w-4 h-4 text-black" />
                    </div>
                    <span className="font-semibold text-sm text-[#FDB022]">
                      Something Property
                    </span>
                  </div>
                  <CardTitle className="text-2xl">Create Seller Account</CardTitle>
                  <CardDescription>
                    Register to list and manage your properties
                  </CardDescription>
                </motion.div>
              )}

              {view === 'seller-login' && (
                <motion.div key="login-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#FDB022] flex items-center justify-center">
                      <Home className="w-4 h-4 text-black" />
                    </div>
                    <span className="font-semibold text-sm text-[#FDB022]">
                      Something Property
                    </span>
                  </div>
                  <CardTitle className="text-2xl">Sign In to Seller Account</CardTitle>
                  <CardDescription>
                    Access your seller portal
                  </CardDescription>
                </motion.div>
              )}

              {view === 'verify-email' && (
                <motion.div key="verify-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-lg bg-[#FDB022] flex items-center justify-center">
                      <Mail className="w-4 h-4 text-black" />
                    </div>
                    <span className="font-semibold text-sm text-[#FDB022]">
                      Something Property
                    </span>
                  </div>
                  <CardTitle className="text-2xl">Verify Your Email</CardTitle>
                  <CardDescription>
                    Enter the verification code sent to <span className="font-medium text-foreground">{verifyEmail}</span>
                  </CardDescription>
                </motion.div>
              )}

              {view === 'seller-success' && (
                <motion.div key="success-header" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <CardTitle className="text-2xl">Welcome!</CardTitle>
                  <CardDescription>Your account has been created</CardDescription>
                </motion.div>
              )}
            </AnimatePresence>
          </CardHeader>

          <CardContent className="space-y-6">
            <AnimatePresence mode="wait">
              {/* ===================== MAIN LOGIN ===================== */}
              {view === 'login' && (
                <motion.div
                  key="login-view"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="username">Email / Username</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="username"
                          type="text"
                          placeholder="Enter your email or username"
                          value={username}
                          onChange={(e) => { setUsername(e.target.value); setLoginError(''); }}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90"
                      disabled={isLoading}
                      data-testid="auth-login-submit"
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>

                  {/* Forgot Password Link */}
                  <div className="text-center -mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (onNavigate) {
                          onClose();
                          onNavigate('forgot-password');
                        }
                      }}
                      className="text-sm text-[#FDB022] hover:underline font-medium"
                      data-testid="auth-forgot-password"
                    >
                      Forgot your password?
                    </button>
                  </div>

                  {loginError && (
                    <div data-testid="login-error" className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
                      {loginError}
                    </div>
                  )}

                  {/* ── Seller Sign Up button ── */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border/50" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-2 text-muted-foreground">or</span>
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-[#FDB022]/40 text-[#FDB022] hover:bg-[#FDB022]/10 hover:border-[#FDB022]/60"
                    onClick={() => { setView('seller-signup'); setSignupError(''); }}
                    data-testid="auth-nav-seller-signup"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Seller Sign Up
                  </Button>
                  <p className="text-center text-xs text-muted-foreground -mt-3">
                    If you are a seller, sign up here to list your properties
                  </p>
                </motion.div>
              )}

              {/* ===================== SELLER SIGN UP ===================== */}
              {view === 'seller-signup' && (
                <motion.div
                  key="signup-view"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  {signupError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
                      {signupError}
                      {signupError.toLowerCase().includes('already exists') && (
                        <button
                          type="button"
                          onClick={() => { setView('seller-login'); setSellerLoginEmail(signupEmail); setSellerLoginError(''); }}
                          className="block mt-1.5 text-[#FDB022] hover:underline font-medium"
                        >
                          Sign in to your existing account instead
                        </button>
                      )}
                    </div>
                  )}

                  <form onSubmit={handleSellerSignup} className="space-y-3.5">
                    <div className="space-y-1.5">
                      <Label htmlFor="signup-name">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={signupName}
                          onChange={(e) => setSignupName(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="you@example.com"
                          value={signupEmail}
                          onChange={(e) => setSignupEmail(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-phone">
                        Phone Number <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-phone"
                          type="tel"
                          placeholder="+27 82 123 4567"
                          value={signupPhone}
                          onChange={(e) => setSignupPhone(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-password"
                          type={showSignupPassword ? 'text' : 'password'}
                          placeholder="Min 8 characters"
                          value={signupPassword}
                          onChange={(e) => setSignupPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupPassword(!showSignupPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSignupPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="signup-confirm">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="signup-confirm"
                          type={showSignupConfirm ? 'text' : 'password'}
                          placeholder="Re-enter your password"
                          value={signupConfirm}
                          onChange={(e) => setSignupConfirm(e.target.value)}
                          className={`pl-10 pr-10 ${
                            signupConfirm && signupConfirm !== signupPassword
                              ? 'border-red-500 focus-visible:ring-red-500'
                              : ''
                          }`}
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSignupConfirm(!showSignupConfirm)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSignupConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      {signupConfirm && signupConfirm !== signupPassword && (
                        <p className="text-xs text-red-500">Passwords do not match</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={signupLoading || (signupConfirm !== '' && signupConfirm !== signupPassword)}
                      data-testid="auth-register-seller-submit"
                    >
                      {signupLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          Create Seller Account
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <button
                    type="button"
                    onClick={() => setView('login')}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                    data-testid="auth-back-to-login"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to Sign In
                  </button>

                  <p className="text-center text-xs text-muted-foreground">
                    Already have a seller account?{' '}
                    <button
                      type="button"
                      onClick={() => { setView('seller-login'); setSellerLoginError(''); }}
                      className="text-[#FDB022] hover:underline font-medium"
                      data-testid="auth-nav-seller-login"
                    >
                      Sign in here
                    </button>
                  </p>

                  <div className="rounded-lg bg-muted/30 border border-border/30 p-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Home className="w-3.5 h-3.5 text-[#FDB022]" />
                      Seller Portal
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      List your properties, track viewings, manage offers, and monitor your sales pipeline all in one place.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ===================== SELLER LOGIN ===================== */}
              {view === 'seller-login' && (
                <motion.div
                  key="login-view"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-6"
                >
                  <form onSubmit={handleSellerLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="seller-login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="seller-login-email"
                          type="email"
                          placeholder="Enter your email"
                          value={sellerLoginEmail}
                          onChange={(e) => setSellerLoginEmail(e.target.value)}
                          className="pl-10"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="seller-login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="seller-login-password"
                          type={showSellerLoginPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={sellerLoginPassword}
                          onChange={(e) => setSellerLoginPassword(e.target.value)}
                          className="pl-10 pr-10"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowSellerLoginPassword(!showSellerLoginPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSellerLoginPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={sellerLoginLoading}
                      data-testid="auth-seller-login-submit"
                    >
                      {sellerLoginLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Logging in...
                        </>
                      ) : (
                        <>
                          Sign In
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Forgot Password Link */}
                  <div className="text-center -mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        if (onNavigate) {
                          onClose();
                          onNavigate('forgot-password');
                        }
                      }}
                      className="text-sm text-[#FDB022] hover:underline font-medium"
                      data-testid="auth-seller-forgot-password"
                    >
                      Forgot your password?
                    </button>
                  </div>

                  {sellerLoginError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
                      {sellerLoginError}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => setView('login')}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                    data-testid="auth-seller-back-to-login"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to Sign In
                  </button>

                  <p className="text-center text-xs text-muted-foreground">
                    Don't have a seller account?{' '}
                    <button
                      type="button"
                      onClick={() => { setView('seller-signup'); setSignupError(''); }}
                      className="text-[#FDB022] hover:underline font-medium"
                      data-testid="auth-seller-nav-signup"
                    >
                      Create one here
                    </button>
                  </p>

                  <div className="rounded-lg bg-muted/30 border border-border/30 p-3 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                      <Home className="w-3.5 h-3.5 text-[#FDB022]" />
                      Seller Portal
                    </p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      List your properties, track viewings, manage offers, and monitor your sales pipeline all in one place.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* ===================== EMAIL VERIFICATION ===================== */}
              {view === 'verify-email' && (
                <motion.div
                  key="verify-view"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="rounded-lg bg-[#FDB022]/10 border border-[#FDB022]/30 p-3">
                    <p className="text-sm text-[#FDB022]">
                      We've sent a verification code to your email address. Please check your inbox (and spam folder) and enter the code below.
                    </p>
                  </div>

                  {verifyError && (
                    <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2 text-sm text-red-400">
                      {verifyError}
                    </div>
                  )}

                  <form onSubmit={handleVerifyEmail} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="verify-code">Verification Code</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="verify-code"
                          type="text"
                          placeholder="Enter 6-digit code"
                          value={verifyCode}
                          onChange={(e) => {
                            // Allow only digits
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setVerifyCode(val);
                          }}
                          className="pl-10 text-center text-lg tracking-[0.5em] font-mono"
                          required
                          maxLength={6}
                          autoFocus
                          autoComplete="one-time-code"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#FDB022] hover:bg-[#FDB022]/90 text-black font-semibold"
                      disabled={verifyLoading || verifyCode.length < 6}
                      data-testid="auth-verify-email-submit"
                    >
                      {verifyLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Verify & Sign In
                        </>
                      )}
                    </Button>
                  </form>

                  <div className="flex items-center justify-center gap-1 text-sm">
                    <span className="text-muted-foreground">Didn't receive the code?</span>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      disabled={resendLoading || resendCooldown > 0}
                      className="text-[#FDB022] hover:underline font-medium disabled:opacity-50 disabled:no-underline"
                      data-testid="auth-resend-code-button"
                    >
                      {resendLoading
                        ? 'Sending...'
                        : resendCooldown > 0
                          ? `Resend in ${resendCooldown}s`
                          : 'Resend Code'
                      }
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => setView('login')}
                    className="flex items-center justify-center gap-1.5 w-full text-sm text-muted-foreground hover:text-foreground transition-colors pt-1"
                    data-testid="auth-verify-back-to-login"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to Sign In
                  </button>
                </motion.div>
              )}

              {/* ===================== SUCCESS ===================== */}
              {view === 'seller-success' && (
                <motion.div
                  key="success-view"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center py-6 space-y-4"
                >
                  <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-8 h-8 text-green-500" />
                  </div>
                  <div className="text-center space-y-1">
                    <h3 className="text-lg font-semibold">Account Created!</h3>
                    <p className="text-sm text-muted-foreground">
                      Welcome to Something Property. Redirecting to your portal...
                    </p>
                  </div>
                  <Loader2 className="w-5 h-5 animate-spin text-[#FDB022]" />
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}